<!--
  Screen:    Patient ends the link with their practice
  Route:     n/a (dialog overlay, opened from LaunchLayout's "verknüpft mit …" chrome)
  Layout:    LaunchLayout
  Role:      Patient
  Status:    ✅ Complete
  Inventory: docs/screen-inventory.md#unlink-dialog

  The mirror of the Portal's "Verknüpfung lösen": either side may end the link
  and neither needs the other's agreement.

  Worth being precise in the copy, because the thing people fear here is losing
  their work and that is not what happens. Nothing is deleted on either side —
  the patient keeps every exercise and all their homework, the practice keeps
  everything it saw up to this moment — the two simply stop feeding each other
  from here on. Hence "keinen Einblick mehr in deinen Übungsverlauf" rather
  than anything that sounds like a delete.
-->
<template>
  <Dialog v-model:show="show" variant="medium" closeable blur>
    <ConfirmChoice
      align="left"
      :title="t('title', { name: practiceName })"
      :body="t('body')"
      :busy="busy"
      :error="error"
      @confirm="unlink"
      @cancel="show = false"
    />
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ConfirmChoice from '@/components/ConfirmChoice.vue'
import Dialog from '@/components/Dialog.vue'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'

const { t } = useI18n()
const auth = useAuthStore()

const show = defineModel('show', { required: true, type: Boolean, default: false })

defineProps<{
  /**
   * The practice name, already resolved against its fallback by the caller —
   * `practices.name` is nullable, and LaunchLayout holds the one definition of
   * what to say instead so the chrome label and this title can't disagree.
   */
  practiceName: string
}>()

const busy = ref(false)
const error = ref<string | null>(null)

async function unlink(): Promise<void> {
  if (busy.value) return
  busy.value = true
  error.value = null
  try {
    await api.delete('/patient/link')
    // Both cleared together: `linked` is what the chrome and the homework
    // gates read, `practiceName` only ever meant "the practice we are linked
    // to" and would otherwise go on naming a practice we have left.
    auth.linked = false
    auth.practiceName = null
    show.value = false
  } catch {
    // Deliberately not optimistic. The link is a server-side fact that the
    // practice sees too, so telling the patient it is gone when the request
    // failed would leave the two sides disagreeing about something the
    // patient believes they have decided.
    error.value = t('error')
  } finally {
    busy.value = false
  }
}

// Reopening after a failure should present a clean decision, not the last
// attempt's error still sitting under the title.
watch(show, (open) => {
  if (!open) error.value = null
})
</script>

<i18n>
{
  "de": {
    "title": "Verknüpfung mit {name} auflösen?",
    "body": "Wenn die Verknüpfung gelöst wird, hat die therapeutische Praxis keinen Einblick mehr in deinen Übungsverlauf.",
    "error": "Die Verknüpfung konnte nicht gelöst werden. Bitte versuche es erneut."
  },
  "en": {
    "title": "End the link with {name}?",
    "body": "Once the link is removed, the therapeutic practice can no longer see your exercise history.",
    "error": "The link could not be removed. Please try again."
  }
}
</i18n>
